import React from "react"
import Image from "next/image"
import Link from "next/link"
import styles from "./ElectricPriceToday.module.css"

const Introduction = () => (
  <section>
    <h3>Introducción</h3>
    <p>
      En el mundo actual, donde la energía es una necesidad básica para nuestras
      vidas diarias, estar al tanto del&nbsp;<strong>precio de la luz</strong>{" "}
      es fundamental para&nbsp;<strong>ahorrar en nuestra factura</strong>{" "}
      energética. Con el aumento constante de los costos de electricidad, es más
      importante que nunca conocer las fluctuaciones diarias y las mejores horas
      para consumir energía. En Fiva, te ayudamos a&nbsp;
      <Link href="/sube-tu-factura">comparar las tarifas energéticas</Link> y
      encontrar la opción más económica para tu hogar.
    </p>
  </section>
)

const WhyElectricityPriceChanges = () => (
  <section>
    <h3>¿Por qué varía el precio de la luz cada día?</h3>
    <p>
      El&nbsp;<strong>precio de la luz varía diariamente</strong> debido a
      varios factores:
    </p>
    <ol>
      <li>
        <strong>
          Oferta y demanda de electricidad:&nbsp;La cantidad de electricidad
          disponible en el mercado y la demanda por parte de los consumidores
          afectan el precio. Durante los períodos de alta demanda, como las olas
          de calor o frío extremo, los precios tienden a aumentar.
        </strong>
      </li>
      <li>
        <strong>Coste de los combustibles:&nbsp;</strong>Los precios de los
        combustibles fósiles (gas, carbón, petróleo) utilizados para generar
        electricidad fluctúan en el mercado global, influyendo directamente en
        el coste de la energía.
      </li>
      <li>
        <strong>
          Condiciones meteorológicas: Las condiciones climáticas afectan la
          producción de energía renovable (solar, eólica, hidráulica). Por
          ejemplo, en días nublados o sin viento, la producción de energía
          renovable puede ser baja, lo que aumenta la dependencia de fuentes de
          energía más caras.
        </strong>
      </li>
      <li>
        <strong>
          Políticas energéticas y tarifas reguladas: Las decisiones
          gubernamentales sobre impuestos, subsidios y regulaciones pueden
          afectar significativamente el precio de la electricidad.
        </strong>
      </li>
    </ol>
  </section>
)

const TrafficLight = () => (
  <section>
    <h3>¿Quieres saber las horas con los precios mas baratos de luz?</h3>
    <p>
      Para ayudarte a entender y aprovechar mejor los precios de energía, hemos
      creado un&nbsp;<strong>semáforo de precios</strong> de la luz. Este
      semáforo te muestra de manera clara y sencilla cuáles son las&nbsp;
      <strong>mejores horas del día para consumir energía</strong> y así ahorrar
      en tu factura.
    </p>
    <div className={styles.imageWrap}>
      <Image
        src="/Images/electric/washing.png"
        alt="Semáforo de precios de la luz"
        width={850}
        height={500}
      />
    </div>

    <p>
      👉 ¿Cuáles son las horas más económicas de consumo eléctrico?:&nbsp;
      <Link href="/articulo/cuales-son-las-horas-mas-economicas-de-consumo-electrico-horarios-clave-para-reducir-tu-factura-de-luz">
        Horarios clave para reducir tu factura de luz
      </Link>
      .
    </p>
  </section>
)

const TipsToSave = () => (
  <section>
    <h3>Consejos para ahorrar en tu factura de luz</h3>
    <article>
      <h4>1. Consumo en horas valle</h4>
      <p>
        Aprovecha las&nbsp;<strong>horas con precios más bajos</strong>{" "}
        (indicadas en verde) para realizar tareas que consumen mucha energía,
        como lavar la ropa, usar el lavavajillas o planchar. Programar estos
        electrodomésticos para que funcionen en estas franjas horarias puede
        resultar en un ahorro considerable en tu factura.
      </p>
    </article>
    <article>
      <h4>2. Desconecta los electrodomésticos</h4>
      <p>
        Muchos aparatos continúan consumiendo energía incluso cuando están
        apagados, pero conectados a la corriente.&nbsp;
        <strong>
          Desconectar los electrodomésticos cuando no los estés usando
        </strong>{" "}
        puede reducir significativamente el consumo de energía en modo standby.
        Considera usar regletas con interruptor para facilitar esta tarea.
      </p>
    </article>
    <article>
      <h4>3. Iluminación eficiente</h4>
      <p>
        Sustituye las bombillas incandescentes por&nbsp;
        <strong>bombillas LED</strong>, que consumen menos energía y tienen una
        vida útil mucho más larga. Además,&nbsp;
        <strong>aprovecha al máximo la luz natural</strong> durante el día y
        apaga las luces cuando no sean necesarias.
      </p>
    </article>
    <article>
      <h4>4. Ajusta la temperatura</h4>
      <p>
        Utiliza un&nbsp;<strong>termostato programable</strong> para mantener tu
        hogar a una temperatura confortable sin gastar energía innecesaria.
        Ajustar la calefacción o el aire acondicionado a una temperatura
        moderada, y reducir su uso durante las horas que no estás en casa, puede
        resultar en un ahorro energético significativo.
      </p>
    </article>
    <article>
      <h4>5. Electrodomésticos eficientes</h4>
      <p>
        Invierte en electrodomésticos de alta eficiencia energética. Aunque
        pueden tener un costo inicial más alto, su consumo de energía es menor a
        lo largo de su vida útil, lo que se traduce en ahorros a largo plazo.
      </p>
    </article>
    <article>
      <h4>6. Aislamiento del hogar</h4>
      <p>
        Mejorar el aislamiento de tu hogar puede reducir la necesidad de
        calefacción y aire acondicionado. Sellar puertas y ventanas, y aislar
        techos y paredes, ayuda a mantener la temperatura interior sin depender
        tanto de sistemas de climatización.
      </p>
    </article>
  </section>
)

const FivaTariffComparator = () => (
  <section>
    <h3>Compara tarifas con Fiva</h3>
    <p>
      En Fiva, te ofrecemos una herramienta fácil de usar para&nbsp;
      <Link href="/sube-tu-factura">comparar las tarifas energéticas</Link> de
      diferentes proveedores. Simplemente ingresa tu consumo y te mostraremos
      las mejores opciones para que puedas elegir la tarifa que más te conviene.
    </p>
    <div className={styles.imageWrap}>
      <Image
        src="/Images/electric/happyfamily.png"
        alt="Comparador de tarifas de Fiva"
        width={850}
        height={500}
      />
    </div>

    <p>
      👉 ¡Ahorra dinero en tu factura de luz y encuentra la mejor tarifa con el
      comprador de tarifas energéticas de Fiva! Comparar es fácil, rápido y
      puede hacer una gran diferencia en tu bolsillo. ¡Empieza a ahorrar hoy
      mismo!
    </p>
  </section>
)

const HowFivaWorks = () => (
  <section>
    <h3>¿Cómo funciona Fiva?</h3>
    <ol>
      <li>
        <strong>Sube tu factura:&nbsp;</strong>Sube tu factura o introduce tus
        datos de consumo energético en nuestra plataforma. Puedes encontrar esta
        información en tus facturas de electricidad.
      </li>
      <li>
        <strong>
          Comparación de tarifas:&nbsp;Fiva analiza diferentes tarifas ofrecidas
          por múltiples proveedores, teniendo en cuenta tus patrones de consumo
          y preferencias.
        </strong>
      </li>
      <li>
        <strong>
          Selección de la mejor opción:&nbsp;Te presentamos las opciones más
          económicas y te ayudamos a cambiar a la tarifa que mejor se adapte a
          tus necesidades.
        </strong>
      </li>
      <li>
        <strong>
          Ahorro garantizado:&nbsp;Al elegir la tarifa adecuada, puedes reducir
          considerablemente tu factura de electricidad.
        </strong>
      </li>
    </ol>
    <p>
      Visita nuestro sitio web y empieza a ahorrar en tu factura de luz hoy
      mismo. Si tienes cualquier duda, no dudes en contactarnos a través de
      nuestro&nbsp;
      <Link href="/contacto">formulario de contacto</Link> en Fiva. Estamos aquí
      para ayudarte a encontrar la mejor solución energética para tu hogar o
      negocio.
    </p>
  </section>
)

const ElectricityPriceToday = () => (
  <section className={styles["rich-text-container"]}>
    <Introduction />
    <br />
    <WhyElectricityPriceChanges />
    <br />
    <TrafficLight />
    <br />
    <TipsToSave />
    <br />
    <FivaTariffComparator />
    <br />
    <HowFivaWorks />
    <br />
  </section>
)

export default ElectricityPriceToday
